/* eslint-disable no-underscore-dangle */
import { observable, transaction } from 'mobx';
import set from 'lodash/set';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import UIModels from '@src/slide/domain/ui-model/element-modeles';
import {
    ElementBaseSdkTypeV3, IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ITempElementManager } from '@src/slide/application/service/temp-element-manager/temp-element-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { ISelectedAnimManager } from '@src/slide/application/service/selection/selected-anim-manager.interface';
import { SlideModelSdkTypeV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { isCtrlKeyDown } from '@src/utils/key-mouse-event/ctrl-key';
import { DeltaOf, IConnectorData, ICTGroupTransform2D, IShapeData } from '@tencent/slide-sdk-model';
import { ElementCommonPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/element-common-property-converter';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { GroupChildFrameConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/frame/group-child-frame-converter';
import { cloneDeep } from '@tencent/slide-sdk-model/src/utils/clone-deep';
import { IGroupModelProps } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/group-model-props.interface';


export default class TempElementManager implements ITempElementManager {
    public isCopyAndMoving: boolean;

    @observable
    public elements: Array<IElementBaseModel> = [];

    @observable
    private lastOffset = {
        x: 0,
        y: 0,
    };

    private selectionService: ISelectionService | undefined;
    private selectedAnims: ISelectedAnimManager | undefined;

    public constructor() {
        this.isCopyAndMoving = false;
    }

    public isMoveWithCopy(e): boolean {
        return isCtrlKeyDown(e);
    }

    public addSelectElements(): void {
        const selectedArray = this.selectionService?.selectedElements.getSortedSelectedArray();
        if (selectedArray && selectedArray.length > 0) {
            const copiedUIModels: Array<IElementBaseModel> = [];
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < selectedArray.length; i++) {
                const model = selectedArray[i];
                if (isIframeShapeModel(model)) {
                    continue;
                }

                const copiedUIModel: IElementBaseModel = UIModels.createUIModel(model.type);
                const copiedSdkModel = model.copyModelHandler()
                    .getCopiedUiModelWithSdkModel(copiedUIModel) as IElementBaseModel;
                if (copiedSdkModel) {
                    this.addModelStore(copiedSdkModel);
                    copiedUIModels.push(copiedSdkModel);
                }
                copiedUIModel.props.style = {
                    ...copiedUIModel.props.style,
                    ...model.getFrame().frameOnSlide(),
                };
            }
            this._add(...copiedUIModels);
        }
    }

    public setSelectionServiceDelegate(selectionService: ISelectionService): void {
        if (this.selectionService) return;
        this.selectionService = selectionService;
    }

    public setSelectedAnimsDelegate(selectedAnims: ISelectedAnimManager): void {
        if (this.selectedAnims) return;
        this.selectedAnims = selectedAnims;
    }

    public clear(): void {
        this.isCopyAndMoving = false;
        if (this.elements.length > 0) {
            this.lastOffset = {
                x: 0,
                y: 0,
            };
            transaction(() => {
                this.elements.splice(0, this.elements.length);
            });
        }
    }

    public move(x: number, y: number): void {
        if (x === this.lastOffset.x && y === this.lastOffset.y) {
            return;
        }
        transaction(() => {
            for (let i = 0; i < this.elements.length; i++) {
                const element = this.elements[i];
                // 决定样式
                element.props.style.left += (x - this.lastOffset.x);
                element.props.style.top += (y - this.lastOffset.y);
                // 触发刷新
                const operationFrame = {
                    width: element.props.style.width,
                    height: element.props.style.height,
                    left: element.props.style.left,
                    top: element.props.style.top,
                };
                set(element, 'operationFrame', operationFrame);
            }
            this.lastOffset = {
                x,
                y,
            };
        });
    }

    public pasteElements(): void {
        const newElementSdkData: Array<IShapeData> = [];
        for (let i = 0; i < this.elements.length; i++) {
            const element = this.elements[i];
            const elementSdkData = cloneDeep((element.sdkData as ElementBaseSdkTypeV3)?.getOwnedData()) as IShapeData;
            elementSdkData.nvPr.ph = undefined;
            if (element.type === UIModelType.CONNECTOR) {
                (elementSdkData as unknown as IConnectorData).nvPr.endCxn = undefined;
                (elementSdkData as unknown as IConnectorData).nvPr.stCxn = undefined;
            }
            // 将坐标更新成拖动后的
            ElementCommonPropertyConverter.convertPartialDataFromUiToSdk(element, elementSdkData, element);
            if (element.type === UIModelType.GROUP) {
                const props = element.props as IGroupModelProps;
                if (props?.childFrame) {
                    if (!elementSdkData.spPr?.xfrm) {
                        elementSdkData.spPr = { xfrm: { } };
                    }
                    const xfrm = elementSdkData.spPr.xfrm as DeltaOf<ICTGroupTransform2D>;
                    GroupChildFrameConverter.convertPartialDataFromUiToSdk(props.childFrame, xfrm);
                }
            }
            elementSdkData && newElementSdkData.push(elementSdkData);
        }

        transaction(() => {
            // @ts-ignore
            (this.selectionService?.currentSlide?.sdkData as SlideModelSdkTypeV3)?.api.addShapes(newElementSdkData);
            if (newElementSdkData.length > 0 && this.selectionService?.currentSlide
                && this.selectionService.currentSlide.children?.length > 0) {
                const { children } = this.selectionService.currentSlide;
                if (children.length - newElementSdkData.length > 0) {
                    const addedElement = children.slice(children.length - newElementSdkData.length, children.length);
                    this.selectionService.selectedElements.clear();
                    this.selectedAnims?.clear();
                    this.selectionService.selectedElements.add(...addedElement);
                }
            }
        });
    }

    public getTempElements(): Array<IElementBaseModel> {
        return this.elements.slice();
    }

    private _add(...items: Array<IElementBaseModel>): void {
        transaction(() => {
            if (items.length > 0) {
                this.isCopyAndMoving = true;
                items.forEach((item) => {
                    if (item && !this._find(item)) {
                        this.elements.push(item);
                    }
                });
            }
        });
    }

    private _find(item: IElementBaseModel) {
        for (let i = 0; i < this.elements.length; i++) {
            if (item.key === this.elements[i].key) {
                return true;
            }
        }
        return false;
    }

    private addModelStore = (model) => {
        if (model.children?.length) {
            model.children.forEach(child => this.addModelStore(child));
        }
    };
}
